<script setup lang="ts">
import { SuiIcon } from '@developers/scoro-ui';
import AppConfirmationPopover from '@/features/App/AppConfirmationPopover.vue';
import { useTranslation } from '@/composables';

export type FieldValueMapping = {
	scoroValue: string;
	newValue: string;
};

export type FieldMappingData = {
	fieldId: string;
	fieldLabel: string;
	valueMappings: FieldValueMapping[];
};

export type MappingItem = {
	id: string;
	name: string;
	fieldMappings?: FieldMappingData[];
};

export type AvalaraFieldMappingSectionProps = {
	/**
	 * Module where the mapping is applied, e.g. "invoices".
	 */
	module?: string;
	/**
	 * List of mapping configurations to display.
	 */
	mappings: MappingItem[];
};

withDefaults(defineProps<AvalaraFieldMappingSectionProps>(), {
	module: 'invoices',
});

const { t } = useTranslation();

/**
 * Generate a comma-separated summary of mapped fields for a mapping item.
 * Shows the field labels (e.g., "Document status, Tax rates, Unit").
 */
const getFieldsSummary = (mapping: MappingItem): string => {
	if (!mapping.fieldMappings || mapping.fieldMappings.length === 0) {
		return '';
	}

	const fieldLabels = mapping.fieldMappings
		.map((fm) => fm.fieldLabel)
		.filter(Boolean);

	if (fieldLabels.length === 0) {
		return '';
	}

	// Show first few fields and add "..." if there are more
	const MAX_FIELDS_TO_SHOW = 3;
	if (fieldLabels.length > MAX_FIELDS_TO_SHOW) {
		return `${fieldLabels.slice(0, MAX_FIELDS_TO_SHOW).join(', ')}, ...`;
	}

	return fieldLabels.join(', ');
};

const emit = defineEmits<{
	(event: 'edit', mapping: MappingItem): void;
	(event: 'add'): void;
	(event: 'copy', mapping: MappingItem): void;
	(event: 'delete', mapping: MappingItem): void;
}>();

const onEditMapping = (mapping: MappingItem) => {
	emit('edit', mapping);
};

const onAddMapping = () => {
	emit('add');
};

const onCopyMapping = (mapping: MappingItem) => {
	emit('copy', mapping);
};

const onConfirmDelete = (mapping: unknown) => {
	emit('delete', mapping as MappingItem);
};
</script>

<template>
	<div class="avalara-field-mapping-section">
		<!-- Mapping List -->
		<div class="avalara-field-mapping-section__list">
			<div
				v-for="mapping in mappings"
				:key="mapping.id"
				class="avalara-field-mapping-section__item"
			>
				<div class="avalara-field-mapping-section__item-content">
					<span class="avalara-field-mapping-section__item-name">
						{{ mapping.name }}
					</span>
					<span
						v-if="getFieldsSummary(mapping)"
						class="avalara-field-mapping-section__item-fields"
					>
						{{ getFieldsSummary(mapping) }}
					</span>
				</div>
				<div class="avalara-field-mapping-section__item-actions">
					<button
						type="button"
						class="avalara-field-mapping-section__action-btn"
						:title="t('edit')"
						@click="onEditMapping(mapping)"
					>
						<SuiIcon name="icon-pencil-16" />
					</button>
					<button
						type="button"
						class="avalara-field-mapping-section__action-btn"
						:title="t('avalara_copy_suffix')"
						@click="onCopyMapping(mapping)"
					>
						<SuiIcon name="icon-copy-16" />
					</button>
					<div class="avalara-field-mapping-section__delete-wrapper">
						<AppConfirmationPopover
							v-slot="{ openConfirmation }"
							:title="t('delete_mapping')"
							message="general_delete_confirmation_message"
							@confirm="onConfirmDelete"
						>
							<button
								type="button"
								class="avalara-field-mapping-section__action-btn avalara-field-mapping-section__action-btn--delete"
								:title="t('delete')"
								@click="openConfirmation(mapping)"
							>
								<SuiIcon name="icon-bin-16" />
							</button>
						</AppConfirmationPopover>
					</div>
				</div>
			</div>

			<!-- Add Mapping Button -->
			<div class="avalara-field-mapping-section__add-row">
				<button
					type="button"
					class="avalara-field-mapping-section__add-btn"
					@click="onAddMapping"
				>
					<SuiIcon name="icon-add-16" />
					<span>{{ t('avalara_add_mapping') }}</span>
				</button>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.avalara-field-mapping-section {
	display: flex;
	flex-direction: column;
	gap: 2rem;
	width: 100%;
	min-width: 0;

	&__list {
		display: flex;
		flex-direction: column;
	}

	&__item {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 1.5rem 1rem;
		border-bottom: 1px solid $neutral-20;

		&:last-of-type {
			border-bottom: none;
		}
	}

	&__item-content {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	&__item-name {
		font-family: Inter, sans-serif;
		font-size: 13px;
		font-weight: 600;
		line-height: 20px;
		color: $neutral-90;
	}

	&__item-fields {
		font-family: Inter, sans-serif;
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;
		color: $neutral-70;
	}

	&__item-actions {
		display: flex;
		align-items: flex-start;
	}

	&__action-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1rem;
		border: none;
		background: transparent;
		cursor: pointer;
		color: $neutral-60;

		&:hover {
			color: $neutral-70;
		}

		&--delete:hover {
			color: $red-60;
		}
	}

	&__delete-wrapper {
		position: relative;
	}

	&__add-row {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		padding: 1rem 0;
	}

	&__add-btn {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 1rem;
		border: none;
		background: transparent;
		cursor: pointer;
		color: $neutral-70;
		font-family: Inter, sans-serif;
		font-size: 12px;
		font-weight: 400;
		line-height: 16px;

		&:hover {
			color: $neutral-80;
		}

		:deep(.sui-icon) {
			color: inherit;
		}
	}
}
</style>
